import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Clock, Trash2, ChevronRight, ShieldAlert, Loader2 } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { deleteAnalysis, getAnalysis, listAnalyses } from "@/lib/api";
import { Button } from "@/components/ui/button";

export default function History() {
  const { user, ready, config } = useAuth();
  const nav = useNavigate();
  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!ready) return;
    if (config?.enabled && !user) {
      nav("/login");
      return;
    }
    setLoading(true);
    listAnalyses()
      .then(setItems)
      .catch((e) => {
        if (e?.response?.status === 401) {
          nav("/login");
        } else {
          toast.error("Failed to load history");
          setItems([]);
        }
      })
      .finally(() => setLoading(false));
  }, [ready, user, config, nav]);

  const open = async (id) => {
    try {
      const a = await getAnalysis(id);
      nav("/", { state: { imported: a } });
    } catch {
      toast.error("Failed to open analysis");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this analysis? This cannot be undone.")) return;
    try {
      await deleteAnalysis(id);
      setItems((prev) => prev.filter((i) => i.id !== id));
      toast.success("Analysis deleted");
    } catch {
      toast.error("Failed to delete");
    }
  };

  return (
    <div className="relative">
      <div className="grid-bg absolute inset-0 pointer-events-none" />
      <div className="relative z-10 max-w-[1200px] mx-auto px-6 md:px-10 py-10">
        <header className="pb-8 border-b border-[#1e293b]">
          <div className="flex items-center gap-2 text-blue-400 font-mono text-xs tracking-[0.3em] uppercase mb-3">
            <Clock className="h-3.5 w-3.5" />
            <span>analysis_history</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight text-slate-50">
            Past <span className="text-blue-400">analyses</span>
          </h1>
          <p className="mt-4 text-slate-400 text-sm max-w-2xl">
            Every ruleset you&apos;ve analyzed, sorted by newest first. Open any run to view the
            details or diff it against a newer version to see how the policy drifted.
          </p>
        </header>

        <section className="pt-8">
          {loading || items === null ? (
            <div className="flex items-center gap-2 text-slate-500 text-sm font-mono">
              <Loader2 className="h-4 w-4 animate-spin" /> loading…
            </div>
          ) : items.length === 0 ? (
            <div className="border border-[#1e293b] rounded-sm p-10 text-center bg-[#0f172a]/60">
              <ShieldAlert className="h-6 w-6 text-slate-500 mx-auto mb-3" />
              <div className="text-slate-400 text-sm font-mono">
                No analyses yet. Run an inspection to see it listed here.
              </div>
            </div>
          ) : (
            <div className="space-y-2" data-testid="history-list">
              {items.map((it) => (
                <div
                  key={it.id}
                  data-testid={`history-item-${it.id.slice(0, 8)}`}
                  className="bg-[#0f172a] border border-[#1e293b] rounded-sm p-4 flex items-center gap-4 hover:border-blue-500/40"
                  style={{ transition: "border-color 0.15s ease" }}
                >
                  <div className="flex-1 min-w-0">
                    <div className="font-mono text-sm text-slate-100 truncate">
                      {it.filename || "unnamed"}
                    </div>
                    <div className="text-[10px] font-mono tracking-[0.2em] uppercase text-slate-500 mt-1">
                      {new Date(it.created_at).toLocaleString()} · id: {it.id.slice(0, 8)}
                    </div>
                  </div>
                  <div className="hidden md:flex items-center gap-4 font-mono text-xs">
                    <span className="text-slate-500">{it.summary.total} rules</span>
                    <span className="text-red-400">{it.summary.shadowed} shadow</span>
                    <span className="text-amber-400">{it.summary.redundant} redund</span>
                    <span className="text-blue-400">{it.summary.overlap || 0} overlap</span>
                    <span className="text-emerald-400">{it.summary.clean} clean</span>
                  </div>
                  <Button
                    variant="ghost"
                    onClick={() => open(it.id)}
                    className="rounded-sm text-slate-200 hover:bg-[#1e293b]"
                    data-testid={`history-open-${it.id.slice(0, 8)}`}
                  >
                    Open <ChevronRight className="h-3 w-3 ml-1" />
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={() => remove(it.id)}
                    className="rounded-sm text-red-400 hover:bg-red-500/10 hover:text-red-300"
                    data-testid={`history-delete-${it.id.slice(0, 8)}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
